import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight, UserCheck, CheckCircle2 } from "lucide-react";
export default function WorkDirectly() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="work-directly" aria-labelledby="work-directly-heading" ref={ref} className="bg-background py-20 lg:py-28 border-b border-border transition-opacity duration-700 ease-out">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Photo Frame (4:5 aspect ratio) */}
          <div className={`lg:col-span-5 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-border bg-muted/40 shadow-sm relative">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi, independent freelance SEO consultant" loading="lazy" className="w-full h-full object-cover object-top" />
              </div>

              {/* Verified badge tag */}
              <div className="mt-4 flex items-center justify-center sm:justify-start gap-2 text-xs text-muted-foreground font-mono">
                <UserCheck className="w-4 h-4 text-primary" />
                <span>Sole SEO Specialist • Direct Strategic Delivery</span>
              </div>
            </div>
          </div>

          {/* Text Content */}
          <div className={`lg:col-span-7 transition-all duration-700 delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="text-xs font-semibold tracking-wider uppercase text-primary mb-3">
              Independent Practice
            </p>
            <h2 id="work-directly-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              Work Directly With Me
            </h2>

            <div className="mt-6 space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              <p>
                When you hire YE Consultancy, you work directly with me, Yassine
                Essaadi. As an independent SEO freelancer UK businesses rely on,
                I handle your strategy and hands-on work personally. As a dedicated
                professional SEO consultant, there are no account managers,
                no junior hand-offs, and no layers between you and the work being done.
              </p>
              <p>
                I do the technical audit, keyword research, recommendation roadmaps,
                implementation guidance and reporting myself. You have one consistent
                person from your first enquiry through to ongoing progress reviews,
                and I am always honest about what I can and cannot influence in search.
              </p>
            </div>

            {/* Direct engagement highlights */}
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 list-none p-0 m-0">
              <li className="flex items-start gap-2.5 text-sm sm:text-base text-foreground">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>Work directly with me, Yassine Essaadi, on every stage</span>
              </li>
              <li className="flex items-start gap-2.5 text-sm sm:text-base text-foreground">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>No account managers, intermediaries or junior staff hand-offs</span>
              </li>
              <li className="flex items-start gap-2.5 text-sm sm:text-base text-foreground">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>One consistent freelance SEO specialist accountable for delivery</span>
              </li>
              <li className="flex items-start gap-2.5 text-sm sm:text-base text-foreground">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>Direct, honest communication focused on genuine business priorities</span>
              </li>
            </ul>

            {/* CTA Link to About Me */}
            <div className="mt-10 pt-6 border-t border-border flex flex-wrap items-center gap-4">
              <Button asChild variant="outline" size="lg" className="rounded-md">
                <Link to="/AboutMePage">
                  Learn more about me
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}